<template>
	<div class="dropdown">
		<div class="flex items-center cursor-pointer px-3 py-2 rounded-lg hover:bg-accent">
			<span class="dropdown-icon-user h-6 w-5 mr-0.5"></span>
			<span class="dropdown-icon-chevron w-6 h-6"></span>
		</div>

		<div class="dropdown-menu">
			<div class="px-3 pb-2 divide-y" style="border-color: var(--border);">
				<p class="text-xs font-medium pb-2" style="color: var(--foreground);">{{ user.name }}</p>
				
				<div v-if="user.isAdmin || user.isBroker" class="pt-2">
					<p class="text-xs font-medium mb-1.5" style="color: var(--muted-foreground);">
						{{ user.isAdmin ? 'Working as' : 'Client' }}
					</p>
					<themed-client-selector
						:available-clients="availableClients"
						:selected-client-id="selectedClientId">
					</themed-client-selector>
				</div>
				
				<a v-else
					:href="'/my-account'" 
					class="block pt-2 text-xs font-medium" 
					style="color: var(--foreground);">
					{{ clientName }}
				</a>
			</div>

			<ul class="px-2 pb-1 space-y-0.5">
				<li v-if="user.isAdmin">
					<a href="/admin" class="flex items-center px-2 py-1 rounded-lg hover:bg-accent transition-colors">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"/>
						</svg>
						<span class="text-xs font-medium" style="color: var(--foreground);">Admin Panel</span>
					</a>
				</li>
				
				<li>
					<a :href="'/my-account'" 
						class="flex items-center px-2 py-1 rounded-lg hover:bg-accent transition-colors">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
						</svg>
						<span class="text-xs font-medium" style="color: var(--foreground);">My Account</span>
					</a>
				</li>
				
				<!-- Theme Dropdown (without toggle) - Visible on laptop+ screens (≥ 1024px) -->
				<li class="hidden lg:block">
					<div class="flex items-center px-2 py-1">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
						</svg>
						<themed-select
							:value="themeVersion"
							:options="themeOptions"
							@input="handleThemeChange"
							aria-label="Select theme version"
							class="flex-1"
						/>
					</div>
				</li>
				
				<!-- Currency Conversion - Visible on all screens -->
				<li>
					<div class="flex items-center px-2 py-1">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd"/>
						</svg>
						<themed-currency-conversion 
							v-if="currencyData"
							:currency-data="currencyData"
							class="flex-1">
						</themed-currency-conversion>
					</div>
				</li>
				
				<!-- Support Portal - Visible on all screens -->
				<li>
					<a href="/support/portal" class="flex items-center px-2 py-1 rounded-lg hover:bg-accent transition-colors">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
						</svg>
						<span class="text-xs font-medium" style="color: var(--foreground);">Support Portal</span>
					</a>
				</li>
				
				<li v-if="showBillingPortal">
					<a :href="billingRoute" class="flex items-center px-2 py-1 rounded-lg hover:bg-accent transition-colors">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
						</svg>
						<span class="text-xs font-medium" style="color: var(--foreground);">Billing Portal</span>
					</a>
				</li>
				
				<li>
					<a :href="logoutRoute" class="flex items-center px-2 py-1 rounded-lg hover:bg-accent transition-colors">
						<svg class="fill-current w-3.5 h-3.5 mr-2" style="color: var(--muted-foreground);" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd"/>
						</svg>
						<span class="text-xs font-medium" style="color: var(--foreground);">Logout</span>
					</a>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		user: {
			type: Object,
			required: true
		},
		availableClients: {
			type: Array,
			default: () => []
		},
		selectedClientId: {
			type: Number,
			default: null
		},
		clientName: {
			type: String,
			default: ''
		},
		showBillingPortal: {
			type: Boolean,
			default: false
		},
		currencyData: {
			type: [Array, Object],
			default: null
		},
		myAccountRoute: {
			type: String,
			default: '/my-account'
		},
		billingRoute: {
			type: String,
			default: '/billing'
		},
		logoutRoute: {
			type: String,
			default: '/logout'
		}
	},
	data() {
		return {
			themeVersion: 'none',
			themeOptions: [
				{ label: 'Theme: None', value: 'none' },
				{ label: 'Theme: V1', value: 'v1' }
			]
		}
	},
	mounted() {
		// Get saved theme version
		this.themeVersion = localStorage.getItem('reopt_theme_version') || 'v1';
	},
	methods: {
		handleThemeChange(newTheme) {
			const oldTheme = localStorage.getItem('reopt_theme_version');
			
			// Update theme version
			this.themeVersion = newTheme;
			
			// Only proceed if theme actually changed
			if (oldTheme === newTheme) {
				return;
			}
			
			localStorage.setItem('reopt_theme_version', this.themeVersion);
			
			// Set cookie
			document.cookie = `reopt_theme_version=${this.themeVersion}; path=/; max-age=31536000`;
			
			// Sync to server and reload
			axios.post('/user/theme-preference', {
				theme_version: this.themeVersion
			}).then(() => {
				window.location.reload();
			}).catch(error => {
				console.error('Failed to sync theme preference:', error);
				window.location.reload();
			});
		}
	}
}
</script>
